import { ValidationError } from "yup";

import axios from "src/utils/axios";
import { apiErrorHandler } from "src/utils/errorHandler";

export default class API {
  API_BASE_URL = `${process.env.REACT_APP_API_URL}/api/`;
  PATH = null;

  getHeaders() {
    const token = localStorage.getItem("accessToken");

    return {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: token ? `Bearer ${token}` : "",
    };
  }

  async sendRequest({ url, data, method, onError, headerOverrides = {} }) {
    const fullURL = this.getFullURL(url);
    const headers = { ...this.getHeaders(), ...headerOverrides };
    try {
      const response = await axios(fullURL, {
        method: method,
        headers: headers,
        data,
      });
      return response;
    } catch (err) {
      if (err.code === "token_not_valid") {
        const refreshToken = localStorage.getItem("refreshToken");
        if (refreshToken) {
          try {
            const reLogin = await axios.post(
              `${this.API_BASE_URL}token/refresh/`,
              {
                refresh: refreshToken,
              },
            );

            const { access } = reLogin.data;
            localStorage.setItem("accessToken", access);
            axios.defaults.headers.common.Authorization = `Bearer ${access}`;
            return await this.sendRequest({
              url,
              data,
              method,
              onError,
              headerOverrides,
            });
          } catch {
            localStorage.removeItem("refreshToken");
            localStorage.removeItem("accessToken");
          }
        }
      }
      const error = apiErrorHandler(err);
      if (onError) {
        onError(error, err);
      }
      throw Error(error);
    }
  }

  async get({ url, onSuccess, onError }) {
    const response = await this.sendRequest({
      url,
      method: "GET",
      onSuccess,
      onError,
    });
    return response;
  }

  async post({ url, data, headers, onSuccess, onError }) {
    return await this.sendRequest({
      url,
      data,
      headerOverrides: headers,
      method: "POST",
      onSuccess,
      onError,
    });
  }

  async put({ url, data, onSuccess, onError }) {
    return await this.sendRequest({
      url,
      data,
      method: "PUT",
      onSuccess,
      onError,
    });
  }

  async delete({ id, onSuccess, onError }) {
    try {
      const url = this.getDetailURL(id);

      const response = await this.sendRequest({
        url,
        method: "DELETE",
        onSuccess,
        onError,
      });

      if (onSuccess) {
        onSuccess({ success: true }, response);
      }
      return response;
    } catch (err) {
      throw Error(err);
    }
  }

  async list({ params, onSuccess, onError }) {
    const response = await this.get({
      url: this.getListURL(params),
      onSuccess,
      onError,
    });

    if ([200].includes(response.status)) {
      const data = await response.data;
      if (onSuccess) {
        onSuccess(
          {
            count: data.count,
            items: data.results,
            isFetching: false,
          },
          data,
        );
      }

      return response;
    }

    throw Error(response);
  }

  async retrieve({ id, onSuccess, onError }) {
    const response = await this.get({
      url: this.getDetailURL(id),
      onSuccess,
      onError,
    });

    if ([200].includes(response.status)) {
      const data = await response.data;

      onSuccess(data, response);
    }

    throw Error(response);
  }

  async create({ data, headers, onSuccess, onError }) {
    try {
      const response = await this.post({
        url: this.getListURL(),
        data,
        headers,
        onSuccess,
        onError,
      });
      const responseData = await response.data;
      if (onSuccess) {
        onSuccess(responseData, response);
      }
      return response;
    } catch (err) {
      throw Error(err);
    }
  }

  async update({ id, data, onSuccess, onError }) {
    try {
      const response = await this.put({
        url: this.getDetailURL(id),
        data,
        onSuccess,
        onError,
      });
      const result = await response.data;
      if (onSuccess) {
        onSuccess(result, response);
      }
      return result;
    } catch (err) {
      throw Error(err);
    }
  }

  getDetailURL(id) {
    return `${this.PATH}${id}/`;
  }

  getListURL(params) {
    return this.joinParams(this.PATH, params);
  }

  getFullURL(path) {
    const url = new URL(path, this.API_BASE_URL).toString();
    return url;
  }

  joinParams(path, params) {
    const qs = new URLSearchParams(params).toString();
    return `${path}${qs ? `?${qs}` : ""}`;
  }
}
